import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { crudeApi } from '../../helpers/crude_api_helper';
import { buildReportParams } from './reportConstants';

// Server-driven list state for the Crude reports (General / Evaluation).
// Pagination, sorting and filtering all happen on the backend — the API is
// paginated (default pageSize 10) and `sortField` must be a PascalCase name
// from that report's whitelist (anything else -> 400).
//
// There is no standalone search box: `searchText` arrives via the Advance
// Search popup, as part of `filters`.
const useReportData = ({ endpoint, filters }) => {
  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  // A new filter set or sort invalidates the current page position.
  useEffect(() => {
    setPage(1);
  }, [filters, sortField, sortDirection]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize: perPage,
        ...buildReportParams(filters),
      };
      if (sortField) {
        params.sortField = sortField;
        params.sortOrder = sortDirection === 'desc' ? 'Desc' : 'Asc';
      }
      const result = await crudeApi.get(endpoint, params);
      setItems(Array.isArray(result?.items) ? result.items : []);
      setTotalRows(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [endpoint, filters, page, perPage, sortField, sortDirection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // `key` is the column's API sortField (already PascalCase).
  const handleSort = key => {
    if (sortField === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(key);
      setSortDirection('asc');
    }
  };

  return {
    items,
    totalRows,
    loading,
    page,
    perPage,
    sortField,
    sortDirection,
    setPage,
    setPerPage,
    handleSort,
    refetch: fetchData,
  };
};

export default useReportData;
